import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { useTenants } from '../hooks/useTenants';
import { Link } from 'react-router-dom';
import ResponsiveTable from './ResponsiveTable';
import GlossaryPanel from './GlossaryPanel';
import { useGlossary } from '../hooks/useGlossary';
import '../styles/shared.scss';

interface TenantsProps {
    onTenantSelect?: (tenantId: string) => void;
    onCreateTenant?: () => void;
}

const Tenants: React.FC<TenantsProps> = ({ onTenantSelect, onCreateTenant }) => {
    const { data: tenants = [], isLoading, error: queryError } = useTenants();
    const { isOpen, scrollToItem, openGlossary, closeGlossary } = useGlossary();

    if (isLoading) {
        return <div>Loading tenants...</div>;
    }

    if (queryError) {
        return <div>Error loading tenants: {queryError.message}</div>;
    }

    return (
        <div>
            <div className="page-header">
                <div className="page-title-group">
                    <h2 className="page-title">Tenants</h2>
                    <button
                        onClick={() => openGlossary('tenant')}
                        className="info-button"
                        title="View definition"
                    >
                        Info
                    </button>
                </div>
                <button
                    onClick={onCreateTenant}
                    className="create-button"
                    title="Create Tenant"
                >
                    <FontAwesomeIcon icon={faPlus} />
                </button>
            </div>





            {tenants.length === 0 ? (
                <p>No tenants found. Create your first tenant to get started.</p>
            ) : (
                <ResponsiveTable
                    columns={[
                        {
                            key: 'name',
                            header: 'Name',
                            render: (value, row) => (
                                <Link
                                    to={`/tenants/${row.id}`}
                                    onClick={() => onTenantSelect?.(row.id)}
                                    className="table-link"
                                >
                                    {value}
                                </Link>
                            )
                        },
                        { key: 'email', header: 'Email' },
                        { key: 'ownerEmail', header: 'Owner Email' },
                        {
                            key: 'status',
                            header: 'Status',
                            render: (value) => (
                                <span className={`status-badge ${value === 'active' ? 'status-badge--active' : 'status-badge--inactive'}`}>
                                    {value}
                                </span>
                            )
                        },
                        {
                            key: 'shared',
                            header: 'Ownership',
                            render: (value) => (
                                <span className={`status-badge ${value ? 'status-badge--shared' : 'status-badge--owner'}`}>
                                    {value ? 'Shared' : 'Owner'}
                                </span>
                            )
                        },
                        {
                            key: 'verified',
                            header: 'Verified',
                            render: (value) => (
                                <span className={`status-badge ${value ? 'status-badge--active' : 'status-badge--inactive'}`}>
                                    {value ? 'Yes' : 'No'}
                                </span>
                            )
                        },
                        {
                            key: 'createdAt',
                            header: 'Created',
                            render: (value) => new Date(value).toLocaleDateString()
                        }
                    ]}
                    data={tenants}
                />
            )}
            
            <GlossaryPanel 
                isOpen={isOpen} 
                onClose={closeGlossary} 
                scrollToItem={scrollToItem} 
            />
        </div>
    );
};

export default Tenants;